import React, { useRef, useState } from "react";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { STANDARD_AGREEMENT_ACCEPT, StandardAgreement } from "../../services/StandardAgreements";
import { fillWorkflow, Workflow } from "../../services/Workflows";
import { fileProblem, isReady, useFileDrop } from "./compare-utils";

export interface QuickCheck {
  workflow: Workflow;
  standard: StandardAgreement | null;
}

/** One saved compare workflow: drop a document on it, or click to choose one */
const QuickCheckCard: React.FC<{
  check: QuickCheck;
  running: boolean;
  /** Another quick check or comparison is being started */
  locked: boolean;
  onRun: (check: QuickCheck, file: File) => void;
}> = ({ check, running, locked, onRun }) => {
  const { t } = useI18n();
  const c = t.comparisons;
  const input = useRef<HTMLInputElement>(null);
  const [hint, setHint] = useState<string | null>(null);
  const usable = !!check.standard && isReady(check.standard);
  const disabled = !usable || locked;

  const take = (file: File) => {
    const problem = fileProblem(file, c);
    if (problem) {
      setHint(problem);
      return void toast.error(problem);
    }
    setHint(null);
    onRun(check, file);
  };
  const { dragging, handlers } = useFileDrop(take, disabled);
  const choose = () => !disabled && input.current?.click();

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={c.cp.quickAria(check.workflow.name)}
      data-testid="quick-check"
      onClick={choose}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          choose();
        }
      }}
      {...handlers}
      className={cn(
        "flex h-full flex-col gap-2 rounded-lg border bg-card p-4 text-left shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:border-brand/60 hover:bg-muted/40",
        dragging && "border-brand bg-brand-light ring-2 ring-brand",
      )}
    >
      <div className="flex items-start gap-2">
        {running ? (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand" />
        ) : (
          <Zap className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        )}
        <div className="min-w-0">
          <div className="break-words font-medium leading-snug">{check.workflow.name}</div>
          {check.workflow.description && (
            <div className="line-clamp-2 text-xs text-muted-foreground">{check.workflow.description}</div>
          )}
        </div>
      </div>
      <div className="mt-auto space-y-1 text-xs">
        {check.standard && <div className="truncate text-muted-foreground">{c.cp.quickStandard(check.standard.name)}</div>}
        {!usable ? (
          <div className="text-amber-700 dark:text-amber-400">{c.cp.quickNoStandard}</div>
        ) : (
          <div className={cn("font-medium", hint ? "text-red-600 dark:text-red-400" : "text-foreground/80")}>
            {running ? c.cp.quickRunning : (hint ?? c.cp.quickDrop)}
          </div>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept={STANDARD_AGREEMENT_ACCEPT}
        className="hidden"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) take(file);
        }}
      />
    </div>
  );
};

/** The focus text a workflow runs with: its instructions, without the {{document}} placeholder */
export const workflowFocus = (workflow: Workflow) => fillWorkflow(workflow.instructions).trim().slice(0, 8000);

export default QuickCheckCard;
